import { useEffect, useRef, useState, type ReactElement, type Ref } from "react";
import { FRAMES } from "odyssey-shared";
import { GLYPH_SIZES, Glyph, GlyphButton, TAB_BADGE, TabBadge, centreOffset, logical } from "../../ui/kit/index.ts";
import { LogLine } from "./log.tsx";
import type { NameMatch } from "./lookup.ts";
import { CHAT_BAR, livePeeks, nextPeekEnd, peekLines, takePeeks, type PeekState } from "./minimise.ts";
import { scopeIconClass } from "./scope-icon.tsx";
import type { ChatLine } from "./store.ts";
import { SCOPE_TABS, badgeText, type TabCounts } from "./tabs.ts";
import { say } from "../../words.ts";

/**
 * The minimised chat panel's bar and the lines that peek above it (P1.9-T30, ADR-068's amendment "the panel
 * minimises"; the design's `ChatMin`, a visual reference): the chat glyph and "Chat", each scope's unread count
 * behind its icon, and the open button at the right. A tap or a click anywhere on the bar opens the panel; the open
 * button is the keyboard's path and names the action. While the bar is an input the bar's label is `away`: kept in
 * the page off the screen, so Shift Tab from the field still reaches the open button, and the bar shows again as the
 * button takes the focus from the field. The peeking lines are the log's own, drawn in an outline over
 * the world with no frame, each cut to one row, the older of two dimmed; they take no pointer and no focus.
 */

/**
 * The side of a scope icon as the atlas draws it.
 */
const ICON = 10;

/**
 * The bar's content height at rest: the bar inside its frame's edge and padding.
 */
const BAR_INNER = CHAT_BAR.height - FRAMES.hud.insets.top - FRAMES.hud.insets.bottom - 2 * CHAT_BAR.padding;

/**
 * The line the bar's label is set on, the design's 10.
 */
const LABEL_LINE = 10;

/**
 * The counts' row's height: a scope icon's and a badge's, so the row can cut counts that outgrow a narrow bar
 * without cutting them down.
 */
const COUNTS = Math.max(ICON, TAB_BADGE.height);

/**
 * The id of the element the peeking lines sit in.
 */
export const PEEK_ID = "ot-chat-peek";

/**
 * Where a part of the bar sits down the bar's content: in its middle, on whole logical pixels, which for a part
 * taller than the content is above its top.
 *
 * @param height The part's height in logical pixels.
 * @returns Its top margin.
 */
function barTop(height: number): number {
  return centreOffset(BAR_INNER, height);
}

/**
 * The bar.
 *
 * @param props The `counts` of what each scope has not shown, `onOpen`, run by a click on the bar or by the open
 *   button, where the caller keeps the open button to focus it (`openRef`), and whether the bar is `away` while the
 *   entry row takes its place.
 * @returns The bar's content.
 */
export function ChatBar({ counts, onOpen, openRef, away }: { readonly counts: TabCounts; readonly onOpen: () => void; readonly openRef: Ref<HTMLButtonElement>; readonly away: boolean }): ReactElement {
  const unread = SCOPE_TABS.flatMap((scope) => {
    const badge = badgeText(counts[scope]);
    return badge === null ? [] : [{ scope, badge }];
  });
  return (
    <div className={away ? "ot-chat__bar ot-chat__bar--away" : "ot-chat__bar"} data-chat="bar" onClick={onOpen} style={{ height: logical(BAR_INNER), gap: logical(CHAT_BAR.gap), paddingLeft: logical(CHAT_BAR.gap) }}>
      <span className="ot-chat__bar-glyph" style={{ marginTop: logical(barTop(GLYPH_SIZES.chat)) }}>
        <Glyph name="chat" />
      </span>
      <span className="ot-chat__bar-label" style={{ marginTop: logical(barTop(LABEL_LINE)), lineHeight: logical(LABEL_LINE) }}>
        {say("chat.panel")}
      </span>
      <span className="ot-chat__bar-counts" data-chat="bar-counts" style={{ gap: logical(CHAT_BAR.gap), marginLeft: logical(CHAT_BAR.gap), marginTop: logical(barTop(COUNTS)), height: logical(COUNTS) }}>
        {unread.map(({ scope, badge }) => (
          <span key={scope} className="ot-chat__bar-count" role="img" aria-label={say("chat.unread", { scope: say(`chat.scopes.${scope}`), count: badge })} data-chat-count={scope} style={{ gap: logical(1) }}>
            <span className={`ot-chat__bar-icon ${scopeIconClass(scope)}`} aria-hidden="true" style={{ marginTop: logical(centreOffset(COUNTS, ICON)) }} />
            <span className="ot-chat__bar-badge" style={{ marginTop: logical(centreOffset(COUNTS, TAB_BADGE.height)) }}>
              <TabBadge text={badge} />
            </span>
          </span>
        ))}
      </span>
      <span className="ot-chat__bar-open" style={{ marginTop: logical(barTop(CHAT_BAR.open)) }}>
        <GlyphButton glyph="chevron-up" label={say("chat.openChat")} size={CHAT_BAR.open} keepFocus buttonRef={openRef} data={{ "data-chat": "open" }} />
      </span>
    </div>
  );
}

/**
 * The lines peeking above the bar: while the panel is minimised, each line that peeks (`peeks`) and arrives is kept
 * for its time on the page's clock, at most the newest two; one timer runs, to the next line's end, which ends that
 * line whatever the clock then reads, and none runs while nothing peeks. Opening the panel drops them, and a line that arrived while the panel was open never peeks.
 *
 * @param lines The log.
 * @param minimised Whether the panel is minimised.
 * @param now The page's clock.
 * @param self The player's names, whose own Party line does not peek, or null before the welcome.
 * @returns The lines to draw, oldest first.
 */
export function usePeek(lines: readonly ChatLine[], minimised: boolean, now: () => number, self: NameMatch | null = null): readonly ChatLine[] {
  const [peek, setPeek] = useState<PeekState>({ entries: [], through: lines.at(-1)?.id ?? 0 });
  const held = useRef(peek);
  held.current = peek;
  useEffect(() => {
    const newest = lines.at(-1)?.id ?? held.current.through;
    const next: PeekState = minimised ? takePeeks(held.current, lines, now(), self) : held.current.entries.length === 0 && held.current.through === newest ? held.current : { entries: [], through: Math.max(held.current.through, newest) };
    if (next !== held.current) {
      setPeek(next);
    }
  }, [lines, minimised]);
  const end = nextPeekEnd(peek.entries);
  useEffect(() => {
    if (end === null) {
      return undefined;
    }
    const timer = setTimeout(() => setPeek((state) => ({ ...state, entries: livePeeks(state.entries, Math.max(end, now())) })), Math.max(0, end - now()));
    return () => clearTimeout(timer);
  }, [end, peek.entries]);
  return minimised ? peekLines(peek.entries, lines) : [];
}

/**
 * Does nothing: a peeking line's name starts no whisper, since the peek takes no pointer.
 */
function ignore(): void {
  return undefined;
}

/**
 * The peeking lines, the older of two dimmed.
 *
 * @param props The `lines` peeking, oldest first, the player's own name (`self`) and the first names the log
 *   shares (`shared`), as the log's lines take them.
 * @returns The lines, in a log region assistive technology reads as they arrive.
 */
export function PeekLines({ lines, self, shared }: { readonly lines: readonly ChatLine[]; readonly self: NameMatch | null; readonly shared: ReadonlySet<string> }): ReactElement {
  return (
    <div className="ot-chat__peek" id={PEEK_ID} role="log" aria-label={say("chat.panel")} data-chat="peek" style={{ gap: logical(CHAT_BAR.peekGap) }}>
      {lines.map((line, index) => (
        <div key={line.id} className="ot-chat__peek-line" data-chat-peek={index < lines.length - 1 ? "older" : "newest"} style={{ height: logical(CHAT_BAR.peekLine) }}>
          <LogLine line={line} self={self} shared={shared} onName={ignore} />
        </div>
      ))}
    </div>
  );
}
